// Repaints a library character for an AI template: new skin tone, hair colour and clothes colours,
// chosen by the API (plan.cast.presenter.look, AILibrary.Look). The character keeps its drawing,
// animations, poses and talking mouth; only colours change.
//
// The parts are found by the names in the character files: most library characters have groups
// named Skin_ID, Hair_ID, Shirt_ID, Coat_ID, Bottom_ID...; the others are read by layer name (Face,
// Hand_L, Hair, Shirt, Hip, Leg_L...). A colour is only changed inside its own part, so a shoe that
// happens to have the hair colour keeps it. Every colour of a part moves together (shades and
// highlights keep their relation to the main colour), so the shading stays right. Eyes, mouths,
// glasses and dark outlines are never changed; on clothes, trims and prints keep their colours.
//
// The repaint is worked out once from the character's default animation (repaintFor) and used for
// all its poses (applyRepaint), so the character looks the same in every scene.

const GROUP_PARTS = [
  [/^Skin\d*_ID$/i, "skin"],
  [/^(Hair\d*|Beard\d*|Mostache\d*|Moustache\d*)_ID$/i, "hair"],
  [/^EyeBro\w*_ID$/i, "brow"],
  [/^(Coat|Jacket|Blazer)\d*_ID$/i, "coat"],
  [/^(Shirt|Top|Dress|TShirt|Sweater|Hoodie)\d*_ID$/i, "top"],
  [/^(Bottom|Pant|Pants|Skirt|Trouser|Trousers)\d*_ID$/i, "bottom"],
];
const PROTECTED_GROUPS = /^(Eye(?!Bro)|Glasses|Mask|EarBud|Teeth|Tongue|Mouth)\w*_ID$/i;

// Characters without named groups: parts by layer name.
const LAYER_PARTS = [
  [/^(Hair\d*|Hair_Back|Beard|Mostache|Moustache)$/i, "hair"],
  [/^EyeBro/i, "brow"],
  [/^(Coat|Jacket)$/i, "coat"],
  [/^(Shirt|Body|Dress|Top|Arm_[LR]|ForeArm_[LR]|[LR]_Arm|[LR]_ForeArm)$/i, "top"],
  [/^(Hip|Leg_[LR]|Calf_[LR]|[LR]_Leg|[LR]_Calf)$/i, "bottom"],
];
const FACE_LAYER = /^(Face|Head)$/i;
const SKIN_LAYERS = /^(Hand_[LR]|[LR]_Hand|Neck|Ear_[LR])$/i;
const PROTECTED_LAYERS = /^(Mouth|Eye(?!Bro)|EyeShut|Glasses|Teeth|Tongue|Mask|EarBud)/i;
// Where a headscarf is drawn on characters with named groups (it has no group of its own).
const SCARF_LAYERS = /^(Cap|Hat|Hair\d*|Hair_Back|Face|Head|Shirt|Scarf|Hijab)$/i;

// ------------------------------------------------------------------ colours

const hexOf = (c) =>
  "#" + c.slice(0, 3).map((v) => Math.max(0, Math.min(255, Math.round(v * 255))).toString(16).padStart(2, "0")).join("");

function rgbOfHex(hex) {
  const n = parseInt(String(hex).replace("#", ""), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

function toHsl([r, g, b]) {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: h * 60, s, l };
}

function toRgb({ h, s, l }) {
  if (s === 0) return [l, l, l];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const k = (t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const hh = h / 360;
  return [k(hh + 1 / 3), k(hh), k(hh - 1 / 3)];
}

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const chroma = ([r, g, b]) => Math.max(r, g, b) - Math.min(r, g, b);
const isNeutral = (hex) => chroma(rgbOfHex(hex)) < 0.06;
const hueGap = (a, b) => {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
};

/**
 * Moves a colour the way the part's main colour moves from base to target: shades stay
 * proportionally darker, highlights proportionally lighter, saturation in proportion.
 */
function shift(hex, base, target) {
  const c = toHsl(rgbOfHex(hex));
  const l =
    c.l <= base.l
      ? target.l * (base.l > 0 ? c.l / base.l : 1)
      : target.l + (1 - target.l) * ((c.l - base.l) / Math.max(0.0001, 1 - base.l));
  const s = base.s > 0.05 ? target.s * (c.s / base.s) : target.s + (c.s - base.s);
  return toRgb({
    h: (((target.h + (c.h - base.h)) % 360) + 360) % 360,
    s: clamp(s, 0, 1),
    l: clamp(l, 0.03, 0.97),
  });
}

/** On clothes only shades of the main colour move (similar hue, or greys with a grey main colour). */
function isShade(hex, baseHex) {
  if (isNeutral(baseHex)) return isNeutral(hex);
  if (isNeutral(hex)) return false;
  return hueGap(toHsl(rgbOfHex(hex)).h, toHsl(rgbOfHex(baseHex)).h) <= 45;
}

// ------------------------------------------------------------------ walking the animation

function allLayers(animation) {
  const out = [...(animation.layers || [])];
  (animation.assets || []).forEach((a) => Array.isArray(a.layers) && out.push(...a.layers));
  return out;
}

/** The colour values of a fill / stroke (static, or each keyframe). */
function colourValues(item) {
  const k = item.c && item.c.k;
  if (!Array.isArray(k) || k.length === 0) return [];
  if (typeof k[0] === "number") return [k];
  const out = [];
  k.forEach((kf) => {
    if (kf && Array.isArray(kf.s) && typeof kf.s[0] === "number") out.push(kf.s);
    if (kf && Array.isArray(kf.e) && typeof kf.e[0] === "number") out.push(kf.e);
  });
  return out;
}

/** Calls visit(value, { ty, layer, group, guarded }) for every fill and stroke colour. */
function eachColour(animation, visit) {
  const walk = (shapes, ctx) =>
    (shapes || []).forEach((s) => {
      if (!s) return;
      if (s.ty === "gr") {
        const name = String(s.nm || "").trim();
        const found = GROUP_PARTS.find(([re]) => re.test(name));
        walk(s.it, { ...ctx, group: found ? found[1] : ctx.group, guarded: ctx.guarded || PROTECTED_GROUPS.test(name) });
      } else if (s.ty === "fl" || s.ty === "st") {
        colourValues(s).forEach((v) => visit(v, { ...ctx, ty: s.ty }));
      }
    });
  allLayers(animation).forEach((l) => {
    const layer = String(l.nm || "").trim();
    walk(l.shapes, { layer, group: null, guarded: PROTECTED_LAYERS.test(layer) });
  });
}

const layerPart = (layer) => (LAYER_PARTS.find(([re]) => re.test(layer)) || [])[1] || null;

/** The part a colour belongs to (or null: left as drawn). */
function partOf(hex, ctx, r) {
  if (ctx.group === "skin") return "skin"; // eyelids are skin too
  if (ctx.guarded || r.outlines.has(hex)) return null;
  if (ctx.group) return ctx.group;
  if (r.byLayers) {
    if (r.skin.has(hex)) return "skin";
    return layerPart(ctx.layer);
  }
  // Named-group characters: a headscarf is drawn without a group.
  if (r.hijab && SCARF_LAYERS.test(ctx.layer) && !isNeutral(hex) && !r.skin.has(hex)) return "scarf";
  return null;
}

/**
 * Works out the repaint of a character for a look ({ skin, hair, top, coat, bottom: "#rrggbb" or
 * missing; hijab: true when "hair" is a headscarf }). Null when nothing changes.
 */
export function repaintFor(animation, look) {
  if (!animation || !look) return null;
  const fills = {};
  const strokes = {};
  const groupSkin = new Set();
  const face = new Set();
  const skinRef = new Set();
  let byLayers = true;
  eachColour(animation, (v, ctx) => {
    const hex = hexOf(v);
    if (ctx.ty === "fl") fills[hex] = (fills[hex] || 0) + 1;
    else strokes[hex] = (strokes[hex] || 0) + 1;
    if (ctx.group) byLayers = false;
    if (ctx.group === "skin") groupSkin.add(hex);
    if (!ctx.guarded && !ctx.group && FACE_LAYER.test(ctx.layer)) face.add(hex);
    if (!ctx.guarded && !ctx.group && SKIN_LAYERS.test(ctx.layer)) skinRef.add(hex);
  });

  // Skin colours: from the Skin groups, or the face colours also used on hands / neck.
  let skin = groupSkin;
  if (byLayers) {
    skin = new Set([...face].filter((h) => skinRef.has(h)));
    if (skin.size === 0) skin = new Set(face);
  }
  // Dark colours used mostly for lines are outlines.
  const outlines = new Set(
    Object.keys(strokes).filter((h) => (strokes[h] || 0) > (fills[h] || 0) && toHsl(rgbOfHex(h)).l < 0.15)
  );
  const r = { byLayers, skin, outlines, hijab: !!look.hijab, maps: {} };

  // part -> hex -> { n: times used, layers: layers it is used on }
  const counts = {};
  eachColour(animation, (v, ctx) => {
    const hex = hexOf(v);
    const part = partOf(hex, ctx, r);
    if (!part) return;
    counts[part] = counts[part] || {};
    const c = (counts[part][hex] = counts[part][hex] || { n: 0, layers: new Set() });
    c.n += 1;
    c.layers.add(ctx.layer);
  });

  // The part's main colour: the one on the most body parts (a scarf over head and shoulders, not
  // the cheeks), then the most used.
  const mainColour = (table) =>
    Object.entries(table || {}).reduce(
      (a, b) => (!a || b[1].layers.size > a[1].layers.size || (b[1].layers.size === a[1].layers.size && b[1].n > a[1].n) ? b : a),
      null
    );
  // clothes: only shades of the main colour move; skin and hair move entirely.
  const paint = (part, target, { baseFrom = part, clothes = false } = {}) => {
    const table = counts[part];
    const main = mainColour(counts[baseFrom]);
    if (!target || !table || !main) return;
    const base = toHsl(rgbOfHex(main[0]));
    const goal = toHsl(rgbOfHex(target));
    const map = new Map();
    Object.keys(table).forEach((hex) => {
      if (clothes && !isShade(hex, main[0])) return;
      let next = shift(hex, base, goal);
      // Lines stay darker than the part they outline.
      if ((strokes[hex] || 0) > (fills[hex] || 0)) {
        const o = toHsl(next);
        if (o.l > goal.l * 0.75) next = toRgb({ ...o, l: goal.l * 0.75 });
      }
      map.set(hex, next);
    });
    if (map.size > 0) r.maps[part] = map;
  };
  paint("skin", look.skin);
  paint("hair", look.hair, { clothes: r.hijab });
  if (r.hijab) paint("scarf", look.hair, { clothes: true });
  else paint("brow", look.hair, { baseFrom: "hair" }); // eyebrows follow the hair, not a scarf
  paint("coat", look.coat, { clothes: true });
  paint("top", look.top, { clothes: true });
  paint("bottom", look.bottom, { clothes: true });
  return Object.keys(r.maps).length > 0 ? r : null;
}

/** Repaints an animation of the character (changes it in place) with the result of repaintFor. */
export function applyRepaint(animation, repaint) {
  if (!animation || !repaint) return animation;
  eachColour(animation, (v, ctx) => {
    const hex = hexOf(v);
    const part = partOf(hex, ctx, repaint);
    const next = part && repaint.maps[part] && repaint.maps[part].get(hex);
    if (next) {
      v[0] = next[0];
      v[1] = next[1];
      v[2] = next[2];
    }
  });
  return animation;
}
